/**
 * Prefer the `size`, `weight`, and `color` props for typography tokens instead of
 * baking font size, font weight, or text color into `style` (or web-only class hacks).
 * Use `style` for layout and one-off cases that the props do not cover.
 */
import React from 'react';
import { Text as RNText, Platform, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { DEFAULT_FONT_FAMILY } from '@/constants/config';
import useMacFontScale from '@/hooks/useMacFontScale';
import { scaleTextStyle } from '@/constants/macFontScale';

const UNSAFE_DOM_TAGS = new Set([
  'script',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'style',
  'base',
]);

/** Any normal HTML-ish tag name on web; blocks obvious XSS vectors. */
function isDomTagName(tag) {
  if (!tag || UNSAFE_DOM_TAGS.has(tag)) return false;
  return /^[a-z][a-z0-9-]*$/i.test(tag);
}

/**
 * RN treats most numeric style values as logical pixels; plain DOM + CSS often
 * interprets unitless numbers differently (e.g. `line-height: 34` = 34× font size).
 * Coerce numeric lengths to explicit `px` on the web `tag` path, except keys
 * where numbers are intentionally unitless (flex, opacity, zIndex, …).
 */
const DOM_UNITLESS_NUMBER_PROPS = new Set([
  // Flex / ratio
  'flex',
  'flexGrow',
  'flexShrink',
  'flexPositive',
  'flexNegative',
  'flexOrder',
  'boxFlex',
  'boxFlexGroup',
  'boxOrdinalGroup',
  'aspectRatio',
  // Stacking / alpha
  'zIndex',
  'opacity',
  'elevation',
  // Typography (non-length)
  'fontWeight',
  'fontVariant',
  // Grid indices / counts
  'gridRow',
  'gridRowEnd',
  'gridRowSpan',
  'gridRowStart',
  'gridColumn',
  'gridColumnEnd',
  'gridColumnSpan',
  'gridColumnStart',
  // CSS unitless numerics
  'animationIterationCount',
  'animationDelay',
  'animationDuration',
  'transitionDelay',
  'transitionDuration',
  'borderImageSlice',
  'borderImageWidth',
  'borderImageOutset',
  'columnCount',
  'columns',
  'fillOpacity',
  'floodOpacity',
  'lineClamp',
  'WebkitLineClamp',
  'order',
  'orphans',
  'widows',
  'strokeOpacity',
  'strokeMiterlimit',
  'stopOpacity',
  'zoom',
  'strokeDashoffset',
  'strokeDasharray',
  'tabIndex',
]);

/** Strip UA default margins/padding on semantic HTML when using `tag` on web. */
const DOM_TAG_LAYOUT_RESET = {
  marginTop: 0,
  marginRight: 0,
  marginBottom: 0,
  marginLeft: 0,
  paddingTop: 0,
  paddingRight: 0,
  paddingBottom: 0,
  paddingLeft: 0,
};

function normalizeRnStyleForDom(style) {
  if (style == null || typeof style !== 'object') return style;

  const next = { ...style };

  for (const key of Object.keys(next)) {
    const v = next[key];
    if (v == null) continue;
    if (typeof v === 'object') continue;
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (DOM_UNITLESS_NUMBER_PROPS.has(key)) continue;

    if (key === 'lineHeight') {
      // Preserve legacy CSS multipliers, but treat responsive pixel lengths
      // (e.g. 39.5) as pixels even when they are fractional.
      if (v > 0 && v <= 3) continue;
    }

    next[key] = `${v}px`;
  }

  return next;
}

/** RN Text props that must not be forwarded to DOM elements (web `tag` path). */
const OMIT_DOM_TEXT_PROPS = new Set([
  'selectable',
  'selectionColor',
  'suppressHighlighting',
  'textBreakStrategy',
  'allowFontScaling',
  'maxFontSizeMultiplier',
  'ellipsizeMode',
  'accessible',
  'accessibilityLabel',
  'accessibilityHint',
  'accessibilityState',
  'accessibilityValue',
  'accessibilityActions',
  'onAccessibilityAction',
  'accessibilityLiveRegion',
  'accessibilityViewIsModal',
  'accessibilityElementsHidden',
  'importantForAccessibility',
]);

const FONT_SIZES = {
  xxs: 11,
  xs: 12,
  s: 13,
  n: 14,
  m: 15,
  l: 17,
  xl: 20,
  xxl: 24,
  xxxl: 30,
};

const FONT_WEIGHTS = {
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '500',
  bold: '600',
  extraBold: '700',
};

// iOS: omit fontFamily. The name "System" is not a real face; pairing it with
// fontWeight makes UIKit synthesize a second outline and letters look doubled.
const FONT_FAMILY = Platform.select({
  android: 'Roboto',
  default: DEFAULT_FONT_FAMILY,
});

const createStyles = (colors) => StyleSheet.create({
  fontFamily: FONT_FAMILY ? {
    fontFamily: FONT_FAMILY,
  } : {},

  // Font sizes
  fontSizeXXS: {
    fontSize: FONT_SIZES.xxs,
  },
  fontSizeXS: {
    fontSize: FONT_SIZES.xs,
  },
  fontSizeS: {
    fontSize: FONT_SIZES.s,
  },
  fontSizeN: {
    fontSize: FONT_SIZES.n,
  },
  fontSizeM: {
    fontSize: FONT_SIZES.m,
  },
  fontSizeL: {
    fontSize: FONT_SIZES.l,
  },
  fontSizeXL: {
    fontSize: FONT_SIZES.xl,
  },
  fontSizeXXL: {
    fontSize: FONT_SIZES.xxl,
  },
  fontSizeXXXL: {
    fontSize: FONT_SIZES.xxxl,
  },
  
  // Font weights
  fontWeightLight: {
    fontWeight: FONT_WEIGHTS.light,
  },
  fontWeightNormal: {
    fontWeight: FONT_WEIGHTS.normal,
  },    
  fontWeightMedium: {
    fontWeight: FONT_WEIGHTS.medium,
  },
  fontWeightBold: {
    fontWeight: FONT_WEIGHTS.bold,
  },
  fontWeightExtraBold: {
    fontWeight: FONT_WEIGHTS.extraBold,
  },
  
  // Text colors
  textColorAccent: {
    color: colors.accent_text_color,
  },
  textColorAccentBg: {
    color: colors.accent_color_primary,
  },
  textColorLink: {
    color: colors.text_color_link || '#3157a4',
  },
  textColorPrimary: {
    color: colors.text_color_primary,
  },
  textColorSecondary: {
    color: colors.text_color_secondary,
  },
  textColorTertiary: {
    color: colors.text_color_tertiary,
  },
  textColorAlt: {
    color: colors.text_color_alt,
  },
  textColorDanger: {
    color: colors.color_destructive,
  },
  textColorWhite: {
    color: '#ffffff',
  },
  textColorBlack: {
    color: '#000000',
  },
});

export default function Text({
  children,
  size = 'n',
  color = 'primary',
  weight = 'normal',
  numberOfLines,
  accessibilityRole = 'text',
  accessibilityLevel,
  /** On web: render as this HTML element (`h1`, `p`, `span`, …). Native: `h1`–`h6` set heading a11y. */
  tag,
  style,
  ...rest
}) {
  const { colors } = useTheme();
  const fontScale = useMacFontScale();
  const styles = createStyles(colors);

  const getFontSizeStyle = () => {
    const sizeMap = {
      xxs: styles.fontSizeXXS,
      xs: styles.fontSizeXS,
      s: styles.fontSizeS,
      n: styles.fontSizeN,
      m: styles.fontSizeM,
      l: styles.fontSizeL,
      xl: styles.fontSizeXL,
      xxl: styles.fontSizeXXL,
      xxxl: styles.fontSizeXXXL,
    };
    return sizeMap[size] || styles.fontSizeN;
  };

  const getFontWeightStyle = () => {
    const weightMap = {
      light: styles.fontWeightLight,
      normal: styles.fontWeightNormal,
      medium: styles.fontWeightMedium,
      semibold: styles.fontWeightMedium,
      bold: styles.fontWeightBold,
      extraBold: styles.fontWeightExtraBold,
    };
    return weightMap[weight] || styles.fontWeightNormal;
  };

  const getTextColorStyle = () => {
    const colorMap = {
      accent: styles.textColorAccent,
      accent_bg: styles.textColorAccentBg,
      link: styles.textColorLink,
      primary: styles.textColorPrimary,
      secondary: styles.textColorSecondary,
      tertiary: styles.textColorTertiary,
      alt: styles.textColorAlt,
      danger: styles.textColorDanger,
      white: styles.textColorWhite,
      black: styles.textColorBlack,
    };
    return colorMap[color] || styles.textColorPrimary;
  };

  const normalizedTag =
    typeof tag === 'string' ? tag.toLowerCase().trim() : '';
  const headingMatch = normalizedTag.match(/^h([1-6])$/);
  const headingLevel = headingMatch ? Number(headingMatch[1]) : undefined;

  // React Native Android's Fabric renderer rejects unknown accessibilityRole
  // values with `JSApplicationIllegalArgumentException`, which tears down the
  // React instance and crashes the app. Map web-flavored aliases like
  // "heading"/"h1"–"h6" to RN's valid "header" before forwarding.
  const sanitizeAccessibilityRole = (role) => {
    if (typeof role !== 'string') return role;
    const lower = role.toLowerCase();
    if (lower === 'heading' || /^h[1-6]$/.test(lower)) return 'header';
    return role;
  };

  const a11yProps = {};
  if (headingLevel != null) {
    a11yProps.accessibilityRole = 'header';
    a11yProps.accessibilityLevel = headingLevel;
  } else {
    const resolvedRole = sanitizeAccessibilityRole(accessibilityRole);
    if (resolvedRole !== 'text') {
      a11yProps.accessibilityRole = resolvedRole;
    }
    if (accessibilityLevel != null) {
      a11yProps.accessibilityLevel = accessibilityLevel;
    }
  }

  const useDomTag =
    Platform.OS === 'web' && normalizedTag && isDomTagName(normalizedTag);

  const textStyles = [
    ...(useDomTag ? [DOM_TAG_LAYOUT_RESET] : []),
    styles.fontFamily,
    getFontSizeStyle(),
    getFontWeightStyle(),
    getTextColorStyle(),
    style,
  ];

  const lineClampStyle =
    useDomTag
      ? numberOfLines != null && numberOfLines > 1
        ? {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: numberOfLines,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }
        : numberOfLines === 1
          ? {
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }
          : null
      : null;

  if (useDomTag) {
    const flatStyle = normalizeRnStyleForDom(
      StyleSheet.flatten([...textStyles, lineClampStyle]),
    );
    const domRest = {};
    for (const key of Object.keys(rest)) {
      if (!OMIT_DOM_TEXT_PROPS.has(key)) domRest[key] = rest[key];
    }
    return React.createElement(normalizedTag, { style: flatStyle, ...domRest }, children);
  }

  return (
    <RNText
      style={fontScale === 1 ? textStyles : scaleTextStyle(StyleSheet.flatten(textStyles), fontScale)}
      numberOfLines={numberOfLines}
      {...a11yProps}
      {...rest}
    >
      {children}
    </RNText>
  );
}
